
<template>
    <!-- eslint-disable vue/no-v-html -->
    <article
        class="guideline-text"
        data-allow-anchor
        v-html="content"
    />
    <!-- eslint-enable -->
</template>

<script setup>
import { range, kebabCase } from 'lodash-es';
import { useGuidelineSearchStore } from '~/store/guideline-search';

const route = useRoute();
const guidelineSearchStore = useGuidelineSearchStore();
const { currentHighlightNumber } = storeToRefs(guidelineSearchStore);
const emit = defineEmits(['openNote']);

const props = defineProps({
    content: {
        type: String,
        default: null
    },
    chapterId: {
        type: Number,
        default: null,
        required: false
    }
});

watch(currentHighlightNumber, (newHighlightNumber) => {
    scrollToHighlight(newHighlightNumber);
}, { immediate: true });

// watch(props.content, () => {
//     prepareHtml();
// }, { immediate: true });

// The watcher above is too expensive, content is too large, instead:
watch(
    () => props.chapterId,
    () => {
        if (!props.chapterId) {
            return;
        }
        prepareHtml();
    }, { immediate: true });

onMounted(() => {
    prepareHtml();
});
/**
 * Scroll to a specific highlight on the page.
 *
 * @param newHighlightNumber
 */
async function scrollToHighlight(newHighlightNumber) {
    if (!import.meta.browser) {
        return;
    }
    if (newHighlightNumber === 0) {
        window.scrollTo(0, 0);
        return;
    }

    let activeHighlight = document.querySelector(`[data-search-count="${newHighlightNumber}"]`);
    const allHighlights = document.querySelectorAll('[data-search-highlight]');

    // First, deactivate all highlights on the page
    allHighlights.forEach((highlight) => {
        highlight.setAttribute('data-active', 'false');
    });

    // Try to find the active highlight, retrying up to 10 times
    let retryCount = 0;
    while (!activeHighlight && retryCount < 10) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        activeHighlight = document.querySelector(`[data-search-count="${newHighlightNumber}"]`);
        retryCount++;
    }

    if (!activeHighlight) {
        return;
    }

    // Activate the new highlight
    activeHighlight.setAttribute('data-active', 'true');

    // Scroll to the new highlight
    activeHighlight.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
    });
}

function prepareHtml() {
    if (!import.meta.browser) {
        return;
    }

    const images = document.querySelectorAll('.guideline-text img');
    if (images.length) {
        images.forEach((element) => {
            element.addEventListener('click', function() {
                window.open(element.src);
            });
        });
    }

    const referenceLinks = document.querySelectorAll('.guideline-text a[href^="#note"]');

    if (referenceLinks.length) {
        referenceLinks.forEach((element) => {
            element.addEventListener('click', function(e) {
                e.preventDefault();

                let numbers = [];
                const numberRange = element.href.match('([0-9]+)-([0-9]+)$');
                if (numberRange && numberRange.length) {
                    numbers = range(numberRange[1], Number(numberRange[2]) + 1);
                } else {
                    numbers = [element.href.match('([0-9]+)$')[0]];
                }
                emit('openNote', { numbers });
            });
        });
    }

    // Parse h2/h3 headings and store as subheadings
    parseAndStoreSubheadings();
}

/**
 * Parse h2 and h3 headings from chapter content, inject IDs if missing,
 * and store them in the guideline search store for sidebar navigation.
 */
function parseAndStoreSubheadings() {
    const chapterSlug = route.params.chapterSlug;
    if (!chapterSlug) {
        return;
    }

    const headings = document.querySelectorAll('.guideline-text h2, .guideline-text h3');
    const subheadings = [];

    headings.forEach((heading) => {
        const title = heading.textContent?.trim();
        if (!title) {
            return;
        }

        // Generate and inject ID if missing
        if (!heading.id) {
            heading.id = kebabCase(title);
        }

        subheadings.push({
            title,
            anchor: `#${heading.id}`,
            level: parseInt(heading.tagName.charAt(1), 10)
        });
    });

    guidelineSearchStore.setSubheadings(chapterSlug, subheadings);
}
</script>

<style lang="less">
.guideline-text {
    width: 100%;
    overflow: hidden;

    img {
        max-width: 100%;
        cursor: pointer;
    }

    &, & p, li, em, strong,
    table, td, td p, th, th p {
        font-size: var(--font-size-xs);
        line-height: var(--lineHeight-lg);

        @media @q-lg-min {
            font-size: var(--font-size-sm);
        }
    }

    tbody:first-child tr:first-child td,
    th {
        .table-head;
    }

    h2 {
        .typography-h2();
        margin-bottom: var(--spacing-sm);
    }

    h3 {
        .typography-h4();
        margin-bottom: var(--spacing-xs);
    }

    h4, h5, h6 {
        margin: var(--spacing-xl) 0 var(--spacing-xs) 0;
        .typography-label-secondary();
    }

    h1, h2, h3, h4, h5, h6 {
        & + p {
            margin-top: 0;
        }
    }

    table {
        margin-bottom: var(--spacing-lg);
        max-width: calc(100vw - (2* var(--grid-side-gap-sm)));
    }

    // Highlights
    span[data-search-highlight='true'] {
        display: inline-block;
        padding: 0 .1em;

        font-weight: var(--font-weight-primary-bold);
        background: fade(#65abdc, 20%);
        transition: background var(--transition-fast);

        &[data-active='true'] {
            background: var(--color-secondary);
        }
    }
}

.guideline-text__table {
    max-width: 100%;
    overflow-x: auto;
}
</style>
